<template>
  <div class="relative" @mouseenter="handleEnter" @mouseleave="handleLeave">
    <div
      class="flex cursor-default items-center gap-4 rounded-lg p-2 text-xs"
      :class="{ 'animate-pulse bg-green-100': activeCall }"
    >
      <PhoneIcon class="h-4 w-4" />
      <div v-if="activeCall" class="flex-1">
        <div class="truncate font-bold">
          {{ activeCall.number }}
        </div>
        <div v-if="activeCall.relevantUsers?.[0]" class="truncate">
          {{ activeCall.relevantUsers?.[0].firstName }}
          {{ activeCall.relevantUsers?.[0].lastName }}
        </div>
      </div>
    </div>

    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="isOpen && calls.length > 0"
        class="absolute right-0 z-50 mt-2 w-96 space-y-2 rounded-md border border-gray-200 bg-white p-4 shadow-lg"
        @mouseenter="handleEnter"
        @mouseleave="handleLeave"
      >
        <div v-for="c in calls" :key="c.callId">
          <div class="flex items-center justify-between">
            <div class="truncate text-sm font-semibold text-gray-700">
              <PhoneIcon class="inline-icon mr-2" />{{ c.number }}
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                :title="copiedNumber === c.number ? 'Copied!' : 'Copy number'"
                @click="copyNumber(c.number)"
              >
                <CheckIcon v-if="copiedNumber === c.number" class="h-3 w-3" />
                <CopyIcon v-else class="h-3 w-3" />
              </button>
              <div class="text-xs text-gray-500">{{ timeAgo(c) }}</div>
            </div>
          </div>

          <ul class="space-y-1">
            <li v-for="u in c.relevantUsers" :key="u.id">
              <RouterLink
                :to="{ name: 'user', params: { id: u.id } }"
                class="flex items-center justify-between rounded px-2 py-1 hover:bg-gray-50"
              >
                <div class="flex items-center gap-2 truncate">
                  <UserIcon class="h-3 w-3 text-gray-500" />
                  <span class="truncate">{{ u.firstName }} {{ u.lastName }}</span>
                </div>
              </RouterLink>
            </li>
          </ul>

          <div v-if="c.relevantBookings?.length">
            <ul class="space-y-1">
              <li v-for="b in c.relevantBookings" :key="b.id">
                <RouterLink
                  :to="{ name: 'bookingEdit', params: { id: b.id } }"
                  class="block rounded px-2 py-1 hover:bg-gray-50"
                >
                  <div class="flex items-center justify-between">
                    <div class="truncate">
                      <span class="font-medium">#{{ b.bookingNumber }}</span>
                      <LicencePlate v-if="b.bookedCar?.licencePlate" class="ml-2">{{
                        b.bookedCar?.licencePlate
                      }}</LicencePlate>
                      {{ b.bookedCar?.vehicleType?.make?.name }}
                      {{ b.bookedCar?.vehicleType?.model }}
                    </div>
                  </div>
                  <div class="truncate text-xs text-gray-600">
                    {{ formatDate(b.startDate) }} {{ pickupLabel(b) }}
                  </div>
                  <div class="truncate text-xs text-gray-600">
                    {{ formatDate(b.endDate) }}
                    {{ dropoffLabel(b) }}
                  </div>
                </RouterLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { CheckIcon, CopyIcon, PhoneIcon, UserIcon } from 'lucide-vue-next';
import LicencePlate from './LicencePlate.vue';
import { type Booking, BookingLocationType, type CallDto } from '@carvia/ros-client-types';
import { getLocalizedField } from '@/utils/get-localized-field';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/hooks/use-formatted-date';
import { formatDistanceToNowStrict, subMinutes } from 'date-fns';
import { useCalls } from '@/hooks/use-calls.ts';

const { calls: allCalls } = useCalls();

const reactiveNow = ref(new Date());
onMounted(() => {
  const interval = setInterval(() => {
    reactiveNow.value = new Date();
  }, 60_000);
  onUnmounted(() => clearInterval(interval));
});

const calls = computed(() =>
  [...allCalls.value]
    .sort((a, b) => (b.startedAt > a.startedAt ? 1 : -1))
    .slice(0, 3)
    .filter(
      ({ startedAt, endedAt }) =>
        new Date(endedAt ?? startedAt) > subMinutes(reactiveNow.value, 60),
    ),
);

const isOpen = ref(false);
let hideTimeout: number | null = null;
const { locale } = useI18n();
const copiedNumber = ref<string | null>(null);

function pickupLabel(b: Booking): string {
  return b.pickupLocationType === BookingLocationType.STATION
    ? getLocalizedField(b.pickupStation!.info, locale.value)
    : (b.pickupLocationGeocodedAddress?.displayName ?? '');
}

function dropoffLabel(b: Booking): string {
  return b.dropoffLocationType === BookingLocationType.STATION
    ? getLocalizedField(b.dropoffStation!.info, locale.value)
    : (b.dropoffLocationGeocodedAddress?.displayName ?? '');
}

function handleEnter(): void {
  if (hideTimeout !== null) clearTimeout(hideTimeout);
  isOpen.value = true;
}

function handleLeave(): void {
  if (hideTimeout !== null) clearTimeout(hideTimeout);
  hideTimeout = window.setTimeout(() => {
    isOpen.value = false;
  }, 150);
}

async function copyNumber(n?: string) {
  if (!n) return;
  try {
    await navigator.clipboard.writeText(n);
    copiedNumber.value = n;
    setTimeout(() => {
      if (copiedNumber.value === n) copiedNumber.value = null;
    }, 1500);
  } catch {
    console.error('Could not copy text to clipboard');
  }
}

const activeCall = computed(() => allCalls.value.find((c) => !c.endedAt));

function timeAgo(call: CallDto): string {
  const ts = call?.endedAt ? new Date(call.endedAt) : new Date(call.startedAt);
  return formatDistanceToNowStrict(ts, { addSuffix: true });
}
</script>

<style scoped>
.inline-icon {
  display: inline-block;
  vertical-align: -2px;
}
</style>
